import * as THREE from 'three';
import * as marietta from '../../../../marietta';
import { ItemInfoCollection } from "../../../webcad/collection";
import { IntersectItemType } from '../../../webcad/collection/ItemInfoCollection';
import { ITEM_MODE_NO } from '../../../webcad/Constants';
import { ExtrasType } from "../WebCadDefines";

export class PinDialogUtil {

    /** 3Dワールド座標はm、2D平面座標の元になる値はmmのため換算する */
    private static readonly MM_PER_METER = 1000;

    constructor() {}

    static getCurrentItem(guid: string): marietta.webcad.item.MonicaObject | marietta.webcad.item.parts.Wall {
        const hitItem = marietta.webcad.WebCADGlobal.getItemByGuid(guid);
        if (hitItem) return hitItem;

        const hitWall = PinDialogUtil.getHitWall(guid);
        return hitWall;
    }

    static currentItem(item: marietta.webcad.item.MonicaObject | marietta.webcad.item.parts.Wall): void {
        marietta.webcad.WebCADGlobal.itemManager.currentItem = null;
        marietta.webcad.WebCADGlobal.itemManager.currentWall = null;
        if (item instanceof marietta.webcad.item.parts.Wall) {
            marietta.webcad.WebCADGlobal.itemManager.currentWall = item as marietta.webcad.item.parts.Wall;
            return;
        }
        marietta.webcad.WebCADGlobal.itemManager.currentItem = item;
    }

    static getHitWall(guid: string): marietta.webcad.item.parts.Wall {
      const itemList = [
        ...marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_ROOMDOWN1_NO) as marietta.webcad.item.Room[],
        ...marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_ROOMDOWN2_NO) as marietta.webcad.item.Room[],
        ...marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_ROOM_NO) as marietta.webcad.item.Room[],
      ];
      for (let i = 0; i < itemList.length; i++) {
        const item = itemList[i];
        const index = item.itemInfo.dotList.findIndex((dot) => dot.guid === guid);
        if (index === -1) continue;

        return item.wallObjList[index];
      }
    }

    /**
     * ダイアログに表示する部屋名。
     * preferSaved が false のときは保存済みの部屋名を使わず、ピンの現在位置から再計算する。
     */
    static getItemRoomName(iteminfo: ItemInfoCollection, intersectItem: IntersectItemType, pinInfo?: ItemInfoCollection, preferSaved: boolean = true): string {
        if (!iteminfo) return '-';
        if (preferSaved) {
          const savedRoomName = pinInfo?.auxiliaryData?.auxiliaryDatum?.room_name;
          if (savedRoomName) return savedRoomName;
        }
        if (this.isWallTargetPinType(intersectItem) || this.isFloorTargetPinType(intersectItem) ||this.isCeilingTargetPinType(intersectItem)) {
          return iteminfo.text.length ? iteminfo.text : null;
        }
        if (this.isFittingTargetPinType(intersectItem) && pinInfo) {
          const pinRoom = this.getFittingRoomByPinPosition(pinInfo);
          if (pinRoom) return pinRoom;
        }
        return iteminfo?.parentRoom ? iteminfo?.parentRoom?.itemInfo?.text : null;
    }

    /**
     * ピン自身の座標から、ピンが置かれている部屋名を求める。
     * 建具は壁の中心線上にあり所属部屋が一意に定まらないため、ピン側の座標で判定する。
     */
    private static getFittingRoomByPinPosition(pinInfo: ItemInfoCollection): string | null {
        const room = marietta.webcad.util.WebCADUtil.getRoomAtPoint(new marietta.core.Point(pinInfo.x, pinInfo.y), pinInfo.floor);
        return room ? room.itemInfo.text : null;
    }

    /**
     * 3Dの現在地（ウォークスルーカメラの位置）にある部屋名。
     * 外観モード・3Dモデル未ロード・部屋の外に立っている・部屋名が未設定の場合は null を返す。
     */
    static getRoomNameAtCameraPosition(): string | null {
        const cameras = marietta.view3d.webcad.ThreeView.get()?.wcCameras;
        if (!cameras?.isWalkThroughCameraMode()) return null;

        const world = cameras.camera?.me?.getWorldPosition(new THREE.Vector3());
        if (!world) return null;

        const floor = marietta.view3d.webcad.WebCadItemManager.getFloorFromPoint(world);
        if (!floor) return null;

        const point = this.convertWorldToPlanPoint(world);
        if (!point) return null;

        const room = marietta.webcad.util.WebCADUtil.getRoomAtPoint(point, floor);
        return room?.itemInfo?.text || null;
    }

    /** 3Dワールド座標（m）を2D平面座標（px）に変換する。3Dモデル未ロード時は null */
    private static convertWorldToPlanPoint(world: THREE.Vector3): marietta.core.Point | null {
        const extras = marietta.view3d.webcad.ThreeView.get()?.wcItemmgr?.GltfExtras;
        if (!extras) return null;

        // model_center は左下原点なので changePoint で2Dと同じ左上原点に揃える
        const center = marietta.webcad.WebCADGlobal.itemManager.changePoint(extras.model_center[0], extras.model_center[1]);
        return new marietta.core.Point(
            marietta.webcad.util.WebCADUtil.convertMMToPix(world.x * PinDialogUtil.MM_PER_METER + center.x),
            marietta.webcad.util.WebCADUtil.convertMMToPix(world.z * PinDialogUtil.MM_PER_METER + center.y),
        );
    }

    /** ダイアログに表示するアイテム名 */
    static getItemName(iteminfo: ItemInfoCollection, intersectItem: IntersectItemType): string {
        if (this.isWallTargetPinType(intersectItem)) return '壁';
        if (this.isFloorTargetPinType(intersectItem)) return '床';
        if (this.isCeilingTargetPinType(intersectItem)) return '天井';
        return iteminfo?.text ?? '-';
    }

    static isWallTargetPinType(intersectItem: IntersectItemType): boolean {
        return [ExtrasType.KABE, ExtrasType.KOSI, ExtrasType.HABAKI].some((type) => type === intersectItem?.type);
    }

    static isFloorTargetPinType(intersectItem: IntersectItemType): boolean {
        return ExtrasType.FLOOR === intersectItem?.type;
    }

    static isCeilingTargetPinType(intersectItem: IntersectItemType): boolean {
        return ExtrasType.CEILING === intersectItem?.type;
    }

    static isFittingTargetPinType(intersectItem: IntersectItemType): boolean {
        return [ExtrasType.WIN, ExtrasType.DOOR, ExtrasType.SHUTTER].some((type) => type === intersectItem?.type);
    }

    static isEquTargetPinType(intersectItem: IntersectItemType): boolean {
        return ExtrasType.EQU === intersectItem?.type;
    }

    static isExtTargetPinType(intersectItem: IntersectItemType): boolean {
        return ExtrasType.EXT === intersectItem?.type;
    }
}
